import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  VStack,
  HStack,
  Button,
  Grid,
  GridItem,
  Badge,
} from '@chakra-ui/react';
import {
  FaBullseye,
  FaEye,
  FaArrowRight,
  FaLightbulb,
  FaMedal,
  FaHandshake,
  FaSeedling,
  FaCheckCircle,
  FaChartLine,
  FaMapMarkerAlt,
  FaIndustry,
} from 'react-icons/fa';

import PageHero from '../Components/PageHero';
import SectionHeading from '../Components/SectionHeading';
import Reveal from '../Components/Reveal';

import aboutImage from '../Components/Assets/cafe-cacao.png';
import agroImage from '../Components/Assets/macadamia.jpg';

function About() {
  const values = [
    {
      icon: FaLightbulb,
      title: 'Innovation',
      description:
        'Modernisation constante de nos processus de production pour offrir des solutions toujours plus performantes.',
      color: 'brand.500',
      bg: 'brand.50',
    },
    {
      icon: FaMedal,
      title: 'Qualité',
      description:
        "Rigueur absolue dans le choix des matières premières et le contrôle produit pour garantir l'excellence.",
      color: 'gold.600',
      bg: 'gold.50',
    },
    {
      icon: FaHandshake,
      title: 'Proximité',
      description:
        'Un engagement fort envers les artisans, entrepreneurs et communautés locales pour un développement partagé.',
      color: 'navy.600',
      bg: 'navy.50',
    },
    {
      icon: FaSeedling,
      title: 'Durabilité',
      description:
        'Une industrialisation respectueuse des écosystèmes et des générations futures.',
      color: 'brand.600',
      bg: 'brand.50',
    },
  ];

  const atouts = [
    {
      icon: FaSeedling,
      title: 'Impact ESG',
      text: "Création d'emplois ruraux, soutien aux coopératives et pratiques industrielles durables.",
      color: 'brand.500',
      bg: 'brand.50',
    },
    {
      icon: FaChartLine,
      title: 'Scalabilité',
      text: "Modèle industriel prêt pour une extension : augmentation de la capacité de clouterie et diversification des produits métalliques.",
      color: 'navy.600',
      bg: 'navy.50',
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Sécurité Foncière',
      text: 'Présence établie sur des sites stratégiques : Bafwasende, Komanda, Tsere.',
      color: 'gold.600',
      bg: 'gold.50',
    },
  ];

  const avantages = [
    "Création d'emplois ruraux et soutien aux coopératives",
    "Modèle industriel prêt pour une extension rapide",
    'Présence établie sur des sites stratégiques (Bafwasende, Komanda, Tsere)',
    'Pratiques industrielles durables et responsables',
  ];

  return (
    <Box>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <PageHero
        eyebrow="Profil Corporatif"
        title="TRIMER SARL —"
        accent="Fer de Lance de l'Agro-Industrie"
        description="Une entreprise multisectorielle dynamique, fer de lance de la synergie entre le développement agricole et l'essor industriel en République Démocratique du Congo."
        image={aboutImage}
        icon={FaIndustry}
        cta={{ label: 'Travailler avec nous', to: '/contact', secondary: { label: 'Nos pôles', to: '/agro-business' } }}
      />

      {/* ═══════════════════════ QUI SOMMES-NOUS ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="7xl">
          <Grid templateColumns={{ base: '1fr', lg: '1.05fr 0.95fr' }} gap={{ base: 12, lg: 16 }} alignItems="center">
            <GridItem>
              <Reveal>
                <VStack align="start" spacing={6}>
                  <SectionHeading
                    align="start"
                    eyebrow="Qui sommes-nous"
                    title="Une Entreprise Multisectorielle au Cœur de la RDC"
                    mb={0}
                  />

                  <Text color="gray.600" lineHeight="1.85">
                    <Text as="span" fontWeight="700" color="gray.900">
                      TRIMER SARL
                    </Text>{' '}
                    est une entreprise multisectorielle dynamique, fer de lance de la synergie
                    entre le développement agricole et l'essor industriel en République
                    Démocratique du Congo.
                  </Text>

                  <Text color="gray.600" lineHeight="1.85">
                    Soucieuse de répondre aux besoins croissants du marché national tout en
                    réduisant la dépendance aux importations, TRIMER SARL s'engage à offrir des
                    produits fiables, durables et accessibles. Qu'il s'agisse de{' '}
                    <Text as="span" fontWeight="700" color="brand.600">
                      sécurité alimentaire
                    </Text>{' '}
                    ou de{' '}
                    <Text as="span" fontWeight="700" color="navy.600">
                      fournitures pour le bâtiment
                    </Text>
                    , notre approche repose sur l'innovation, la qualité de production locale et
                    une vision claire d'industrialisation durable.
                  </Text>

                  <HStack spacing={4} pt={2} flexWrap="wrap">
                    <Button as={RouterLink} to="/contact" size="lg" rightIcon={<FaArrowRight />}>
                      Travailler avec nous
                    </Button>
                    <Button as={RouterLink} to="/industries" size="lg" variant="outline" borderColor="gray.300" color="gray.700">
                      Découvrir la clouterie
                    </Button>
                  </HStack>
                </VStack>
              </Reveal>
            </GridItem>

            <GridItem>
              <Reveal delay={0.12}>
                <Box position="relative">
                  <Box
                    h={{ base: '320px', md: '440px' }}
                    bgImage={`url(${agroImage})`}
                    bgSize="cover"
                    bgPosition="center"
                    borderRadius="2xl"
                    position="relative"
                    overflow="hidden"
                    boxShadow="lift"
                  >
                    <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(5,42,27,0.9), rgba(5,42,27,0.15))" />
                    <VStack position="absolute" bottom={6} left={6} right={6} align="start" spacing={1}>
                      <Text fontWeight="800" fontSize="lg" color="white">
                        TRIMER SARL
                      </Text>
                      <Text fontSize="sm" color="whiteAlpha.800">
                        Production locale d'excellence
                      </Text>
                    </VStack>
                  </Box>

                  <Box
                    position="absolute"
                    top={6}
                    right={-4}
                    display={{ base: 'none', md: 'block' }}
                    bg="white"
                    borderRadius="xl"
                    px={5}
                    py={4}
                    boxShadow="lift"
                  >
                    <HStack spacing={3}>
                      <Icon as={FaSeedling} color="brand.500" boxSize={6} />
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="700" letterSpacing="0.08em">
                          TERRES
                        </Text>
                        <Text fontWeight="800" fontSize="sm">
                          100s d'hectares
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                </Box>
              </Reveal>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════ MISSION / VISION ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="gray.50" position="relative" overflow="hidden">
        <Box position="absolute" inset={0} className="bg-grid-dark" opacity={0.5} pointerEvents="none" />

        <Container maxW="7xl" position="relative">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mb={{ base: 14, md: 20 }}>
            <Reveal>
              <HStack
                p={{ base: 7, md: 9 }}
                bg="white"
                borderRadius="2xl"
                boxShadow="card"
                borderLeft="6px solid"
                borderColor="brand.500"
                spacing={6}
                align="start"
                h="100%"
                transition="all 0.35s"
                _hover={{ transform: 'translateY(-4px)', boxShadow: 'lift' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  h="60px"
                  w="60px"
                  borderRadius="xl"
                  bg="brand.50"
                  flexShrink={0}
                >
                  <Icon as={FaBullseye} boxSize={7} color="brand.500" />
                </Box>
                <Box>
                  <Heading size="md" color="gray.900" mb={2}>
                    Notre Mission
                  </Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.8">
                    Favoriser le développement socio-économique local par la production de denrées
                    agricoles d'excellence et la fabrication d'articles industriels de première
                    nécessité (clouterie). Nous transformons les ressources naturelles de la RDC
                    pour améliorer le quotidien de nos clients, soutenir les petits exploitants
                    agricoles et créer des emplois stables et respectueux de l'environnement.
                  </Text>
                </Box>
              </HStack>
            </Reveal>

            <Reveal delay={0.1}>
              <HStack
                p={{ base: 7, md: 9 }}
                bg="white"
                borderRadius="2xl"
                boxShadow="card"
                borderLeft="6px solid"
                borderColor="navy.500"
                spacing={6}
                align="start"
                h="100%"
                transition="all 0.35s"
                _hover={{ transform: 'translateY(-4px)', boxShadow: 'lift' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  h="60px"
                  w="60px"
                  borderRadius="xl"
                  bg="navy.50"
                  flexShrink={0}
                >
                  <Icon as={FaEye} boxSize={7} color="navy.500" />
                </Box>
                <Box>
                  <Heading size="md" color="gray.900" mb={2}>
                    Notre Vision
                  </Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.8">
                    Devenir le moteur agro-industriel de référence en RDC. Nous aspirons à bâtir
                    un avenir où la souveraineté alimentaire et l'indépendance industrielle
                    convergent pour transformer durablement les communautés,{' '}
                    <Text as="span" fontWeight="700" color="gray.900">
                      « de la ferme à la quincaillerie »
                    </Text>
                    .
                  </Text>
                </Box>
              </HStack>
            </Reveal>
          </SimpleGrid>

          <SectionHeading
            eyebrow="Ce qui nous guide"
            title="Nos Valeurs Fondamentales"
            mb={12}
          />

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
            {values.map((value, index) => (
              <Reveal key={value.title} delay={index * 0.08}>
                <VStack
                  p={8}
                  bg="white"
                  borderRadius="2xl"
                  boxShadow="soft"
                  textAlign="center"
                  border="1px solid"
                  borderColor="gray.100"
                  h="100%"
                  spacing={4}
                  transition="all 0.35s"
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'card', borderColor: 'transparent' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    h="56px"
                    w="56px"
                    borderRadius="xl"
                    bg={value.bg}
                  >
                    <Icon as={value.icon} boxSize={6} color={value.color} />
                  </Box>
                  <Heading size="sm" color="gray.900">
                    {value.title}
                  </Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.75">
                    {value.description}
                  </Text>
                </VStack>
              </Reveal>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ ATOUTS STRATÉGIQUES ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="7xl">
          <SectionHeading
            eyebrow="Atouts stratégiques"
            title="Pourquoi investir dans TRIMER SARL"
            lead="Trois piliers solides qui rendent notre modèle d'investissement attractif et durable."
          />

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {atouts.map((atout, index) => (
              <Reveal key={atout.title} delay={index * 0.1}>
                <Box
                  bg="gray.50"
                  p={8}
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.100"
                  h="100%"
                  transition="all 0.35s"
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'card', bg: 'white', borderColor: 'transparent' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    h="54px"
                    w="54px"
                    borderRadius="xl"
                    bg={atout.bg}
                    mb={5}
                  >
                    <Icon as={atout.icon} boxSize={6} color={atout.color} />
                  </Box>
                  <Heading size="md" color="gray.900" mb={3}>
                    {atout.title}
                  </Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.8">
                    {atout.text}
                  </Text>
                </Box>
              </Reveal>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ PARTENARIAT ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="navy.900" color="white" position="relative" overflow="hidden">
        <Box position="absolute" inset={0} className="bg-grid" opacity={0.35} pointerEvents="none" />

        <Container maxW="7xl" position="relative">
          <Grid templateColumns={{ base: '1fr', lg: '0.95fr 1.05fr' }} gap={{ base: 12, lg: 16 }} alignItems="center">
            <GridItem>
              <Reveal>
                <Box
                  borderRadius="2xl"
                  overflow="hidden"
                  h={{ base: '300px', md: '420px' }}
                  bgImage={`url(${agroImage})`}
                  bgSize="cover"
                  bgPosition="center"
                  position="relative"
                  boxShadow="lift"
                >
                  <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(8,28,52,0.92), rgba(8,28,52,0.25))" />
                  <VStack position="absolute" bottom={6} left={6} right={6} align="start" spacing={1}>
                    <Text fontWeight="800" fontSize="lg" color="white">
                      TRIMER SARL
                    </Text>
                    <Text fontSize="sm" color="whiteAlpha.800">
                      Partenariat pour la croissance
                    </Text>
                  </VStack>
                </Box>
              </Reveal>
            </GridItem>

            <GridItem>
              <Reveal delay={0.12}>
                <VStack align="start" spacing={6}>
                  <Box>
                    <HStack spacing={3} color="gold.300" fontWeight="700" textTransform="uppercase" letterSpacing="0.14em" fontSize="xs" mb={3}>
                      <Box w="28px" h="2px" bg="gold.300" borderRadius="full" />
                      Objectifs de partenariat
                    </HStack>
                    <Heading size="2xl" color="white" fontWeight="800">
                      Rejoignez notre vision
                    </Heading>
                  </Box>

                  <Text color="whiteAlpha.800" lineHeight="1.85">
                    TRIMER SARL recherche des partenaires financiers pour accélérer la
                    mécanisation du pôle agricole, étendre la capacité de production de l'unité
                    de clouterie et certifier les processus aux normes internationales pour
                    faciliter l'exportation directe.
                  </Text>

                  <VStack align="stretch" spacing={3} w="100%">
                    {avantages.map((avantage) => (
                      <HStack key={avantage} spacing={3} align="start">
                        <Icon as={FaCheckCircle} color="brand.300" mt="2px" flexShrink={0} />
                        <Text color="whiteAlpha.800" fontSize="sm">
                          {avantage}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>

                  <HStack spacing={4} pt={4} flexWrap="wrap">
                    <Button
                      as={RouterLink}
                      to="/contact"
                      size="lg"
                      bg="gold.400"
                      color="navy.900"
                      rightIcon={<FaArrowRight />}
                      _hover={{ bg: 'gold.300', transform: 'translateY(-2px)' }}
                    >
                      Devenir partenaire
                    </Button>
                    <Button as={RouterLink} to="/industries" size="lg" variant="ghostOnDark">
                      Voir nos capacités
                    </Button>
                  </HStack>
                </VStack>
              </Reveal>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════ CTA ═══════════════════════ */}
      <Box py={{ base: 16, md: 20 }} bg="white">
        <Container maxW="4xl">
          <Reveal>
            <Box
              borderRadius="3xl"
              bgGradient="linear(120deg, brand.600, brand.800 55%, navy.800)"
              color="white"
              px={{ base: 8, md: 14 }}
              py={{ base: 12, md: 16 }}
              textAlign="center"
              position="relative"
              overflow="hidden"
              boxShadow="lift"
            >
              <Box position="absolute" inset={0} className="bg-grid" opacity={0.3} />
              <Box
                position="absolute"
                top="-90px"
                right="-60px"
                w="260px"
                h="260px"
                borderRadius="full"
                bgGradient="radial(circle at center, rgba(224,174,56,0.35), transparent 70%)"
              />

              <VStack spacing={6} position="relative">
                <Badge bg="whiteAlpha.200" color="white" px={4} py={1.5} fontSize="xs" letterSpacing="0.12em">
                  Investissement
                </Badge>
                <Heading size="xl" color="white" fontWeight="800">
                  Prêt à investir dans l'avenir de la RDC ?
                </Heading>
                <Text opacity={0.9} maxW="xl">
                  Contactez-nous pour découvrir comment TRIMER SARL peut vous accompagner dans
                  vos projets agricoles et industriels.
                </Text>
                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  bg="white"
                  color="brand.800"
                  rightIcon={<FaArrowRight />}
                  _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                >
                  Contactez-nous
                </Button>
              </VStack>
            </Box>
          </Reveal>
        </Container>
      </Box>
    </Box>
  );
}

export default About;
